import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import VideoBlock from "./VideoBlock";
import Reveal from "./Reveal";
import { VIDEOS } from "@/lib/content";

const STEPS = [
  {
    n: "01",
    title: "Mirar",
    text: "Detectamos la realidad sin maquillaje.",
  },
  {
    n: "02",
    title: "Ordenar",
    text: "Quitamos ruido y priorizamos lo importante.",
  },
  {
    n: "03",
    title: "Probar",
    text: "Lanzamos una prueba barata de 7 a 30 días.",
  },
  {
    n: "04",
    title: "Automatizar",
    text: "Metemos IA o herramientas solo cuando el proceso ya funciona.",
  },
];

const Method = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const videoScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 1.06]);
  const videoY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      id="metodo"
      ref={ref}
      data-testid="method-section"
      className="et-section relative overflow-hidden"
    >
      <div className="et-container relative z-10">
        <div className="max-w-3xl">
          <Reveal>
            <div className="et-eyebrow">Método Mr. ET</div>
          </Reveal>

          <Reveal delay={0.08}>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] text-white et-text-balance">
              Del caos al sistema en{" "}
              <span className="et-text-gold">4 pasos.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.16}>
            <p className="mt-4 text-base md:text-lg text-[#f5f2ea]/90 max-w-2xl leading-relaxed">
              Un proceso pensado para pymes reales: simple, medible y útil. Sin
              jerga, sin humo y sin gastar antes de saber qué funciona.
            </p>
          </Reveal>
        </div>

        {/* Steps + Video composition */}
        <div className="mt-10 md:mt-12 grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Steps */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4 md:gap-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div
                  className="et-card h-full"
                  data-testid={`method-step-${i}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="et-num text-xs">{s.n}</span>
                    <span className="h-px flex-1 bg-gradient-to-r from-[#c5a059]/40 to-transparent" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-[#f5f2ea]/85 leading-relaxed">
                    {s.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Video — blended (no border, soft mask into bg) */}
          <motion.div
            style={{ scale: videoScale, y: videoY }}
            className="lg:col-span-5 lg:sticky lg:top-28 relative"
          >
            <Reveal>
              <VideoBlock
                src={VIDEOS.method}
                aspect="aspect-[4/5]"
                testId="method-video"
                variant="blend"
              />
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-4 flex items-center justify-center gap-3">
                <span className="et-pulse w-2 h-2 rounded-full bg-[#c5a059]" />
                <p className="text-[11px] md:text-xs uppercase tracking-[0.22em] text-[#f5f2ea]/85">
                  Panel de control · decisiones, no informes
                </p>
              </div>
            </Reveal>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Method;
